/**
 * Concrete Maturity Inputs guide content and sidebar (pages/MaturityInputs.tsx).
 *
 * MaturitySidebarLayout is the page's grey side menu plus the selected section
 * (see maturitySections.ts). The guide content per section:
 *
 *   - introduction   MaturityIntroduction
 *   - astm-c1074     AstmC1074Overview
 *   - nurse-saul     TemperatureTimeFactorInfo (the groups table follows it)
 *   - arrhenius      EquivalentAgeInfo (the groups table follows it)
 *
 * Text is our own summary of ASTM C1074; equations are typeset here rather
 * than copied from the standard. English defaults are inline (other locales
 * fall back to them until translated).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  MenuBook as IntroIcon,
  Description as StandardIcon,
  Thermostat as TtfIcon,
  ShowChart as EaIcon,
  Science as LabIcon,
} from '@mui/icons-material';
import { MATURITY_SECTIONS, isAstmSubsection, sectionLabel, type MaturitySection } from './maturitySections';

/** go: another Maturity Inputs section. open: any other app path. */
export interface GuideNav {
  go: (section: MaturitySection) => void;
  open: (path: string) => void;
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.75, color: 'text.primary' }}>
    {children}
  </Typography>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
    {children}
  </Typography>
);

/** Italic symbol with an optional subscript, e.g. <Sym s="T" sub="a" />. */
const Sym: React.FC<{ s: string; sub?: string }> = ({ s, sub }) => (
  <Box component="span" sx={{ fontStyle: 'italic' }}>
    {s}
    {sub && <Box component="sub" sx={{ fontStyle: 'italic', fontSize: '0.7em' }}>{sub}</Box>}
  </Box>
);

const EquationBox: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    role="math"
    aria-label={label}
    sx={{
      fontFamily: '"Cambria Math", "Times New Roman", serif',
      fontSize: '1.25rem',
      textAlign: 'center',
      py: 2,
      px: 2,
      my: 1.5,
      bgcolor: 'grey.50',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflowX: 'auto',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

/** M(t) = Σ (Ta − T0) Δt */
export const TtfEquation: React.FC = () => (
  <EquationBox label="M of t equals the sum of T a minus T 0, times delta t">
    <Sym s="M" />(<Sym s="t" />) = Σ (<Sym s="T" sub="a" /> − <Sym s="T" sub="0" />) Δ<Sym s="t" />
  </EquationBox>
);

/** te = Σ e^(−Q (1/Ta − 1/Ts)) Δt */
export const EaEquation: React.FC = () => (
  <EquationBox label="t e equals the sum of e to the power of minus Q times one over T a minus one over T s, times delta t">
    <Sym s="t" sub="e" /> = Σ <Sym s="e" />
    <Box component="sup" sx={{ fontSize: '0.7em' }}>
      −<Sym s="Q" />(1/<Sym s="T" sub="a" /> − 1/<Sym s="T" sub="s" />)
    </Box>{' '}
    Δ<Sym s="t" />
  </EquationBox>
);

const VariableTable: React.FC<{ rows: [React.ReactNode, string][] }> = ({ rows }) => (
  <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, my: 1.5 }}>
    {rows.map(([sym, desc], i) => (
      <React.Fragment key={i}>
        <Typography component="dt" variant="body2" sx={{ fontFamily: '"Cambria Math", "Times New Roman", serif', fontSize: '1rem' }}>
          {sym}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, lineHeight: 1.75 }}>
          {desc}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const PageTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
    {children}
  </Typography>
);

const PrevNext: React.FC<{
  prev?: { label: string; to: MaturitySection };
  next?: { label: string; to: MaturitySection };
  go: GuideNav['go'];
}> = ({ prev, next, go }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
    <Box>
      {prev && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => go(prev.to)} sx={{ textTransform: 'none' }}>
          {prev.label}
        </Button>
      )}
    </Box>
    <Box>
      {next && (
        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => go(next.to)} sx={{ textTransform: 'none' }}>
          {next.label}
        </Button>
      )}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Introduction
// ---------------------------------------------------------------------------

export const MaturityIntroduction: React.FC<{ nav: GuideNav }> = ({ nav }) => {
  const { go, open } = nav;
  const { t } = useTranslation();

  const steps: { title: string; body: string; action?: { label: string; onClick: () => void } }[] = [
    {
      title: t('maturityGuide.intro.step1Title', 'Choose a maturity function'),
      body: t(
        'maturityGuide.intro.step1Body',
        'Create a Temperature-Time Factor group (datum temperature) or an Equivalent Age group (activation energy and reference temperature) from the sidebar. Use Calculate Datum Temperature or Q-Value if you have mortar cube results for the mix.',
      ),
      action: { label: t('maturityGuide.intro.step1Action', 'Read about ASTM C1074'), onClick: () => go('astm-c1074') },
    },
    {
      title: t('maturityGuide.intro.step2Title', 'Calibrate the mix'),
      body: t(
        'maturityGuide.intro.step2Body',
        'Cast and test specimens of the mix at several ages while recording their temperature. The strength results against the calculated maturity give the strength-maturity relationship.',
      ),
    },
    {
      title: t('maturityGuide.intro.step3Title', 'Assign it to the mix design'),
      body: t(
        'maturityGuide.intro.step3Body',
        'Select the maturity group on the mix design in Mix / Recipes, so every pour that uses the mix calculates maturity the same way.',
      ),
      action: { label: t('maturityGuide.intro.step3Action', 'Open Mix / Recipes'), onClick: () => open('/products') },
    },
    {
      title: t('maturityGuide.intro.step4Title', 'Monitor the pour'),
      body: t(
        'maturityGuide.intro.step4Body',
        'Sensors in the structure record the concrete temperature. ConXedge calculates maturity in real time and applies the calibration to estimate the in-place strength at each sensor.',
      ),
    },
  ];

  return (
    <Box sx={{ maxWidth: 920 }}>
      <PageTitle>{t('maturityGuide.intro.title', 'Introduction to Concrete Maturity')}</PageTitle>
      <Para>
        {t(
          'maturityGuide.intro.p1',
          'Concrete maturity is an engineering method used to estimate the in-place compressive strength of concrete by accounting for the combined effects of curing time and temperature.',
        )}
      </Para>
      <Para>
        {t(
          'maturityGuide.intro.p2',
          'It is based on the link between cement hydration, temperature and strength gain. Concrete cured at higher temperatures generally gains strength faster than the same concrete cured at lower temperatures. By continuously measuring the internal concrete temperature, a maturity index can be calculated that represents the accumulated thermal history of the concrete.',
        )}
      </Para>
      <Para>
        {t(
          'maturityGuide.intro.p3',
          'Under ASTM C1074, maturity is calculated with either the Temperature-Time Factor method (commonly called Nurse-Saul) or the Equivalent Age method, which is based on the Arrhenius equation.',
        )}
      </Para>
      <Para>
        {t(
          'maturityGuide.intro.p4',
          'This gives a more representative picture of actual strength development than conventionally cured cylinders alone, particularly where the temperature in the structure differs significantly from laboratory or field-cured specimens. It can support decisions such as formwork removal, lifting, prestressing, post-tensioning, loading, and confirming that early-age strength requirements have been met.',
        )}
      </Para>

      <Heading>{t('maturityGuide.intro.howTitle', 'How maturity works in ConXedge')}</Heading>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1 }}>
        {steps.map((s, i) => (
          <Paper key={s.title} variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{s.title}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, flex: 1 }}>{s.body}</Typography>
            {s.action && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={s.action.onClick} sx={{ textTransform: 'none', px: 0 }}>
                  {s.action.label}
                </Button>
              </Box>
            )}
          </Paper>
        ))}
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
        {t(
          'maturityGuide.intro.calibrationNote',
          'A maturity value on its own is not a strength. Strength estimates are only as good as the calibration for that specific mix.',
        )}
      </Alert>

      <PrevNext go={go} next={{ label: t('maturityGuide.nav.astm', 'ASTM C1074'), to: 'astm-c1074' }} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// ASTM C1074 overview
// ---------------------------------------------------------------------------

export const AstmC1074Overview: React.FC<{ nav: GuideNav }> = ({ nav }) => {
  const { go } = nav;
  const { t } = useTranslation();

  const methodCards: { onClick: () => void; key: string; title: string; subtitle: string; body: string }[] = [
    {
      key: 'ttf',
      onClick: () => go('nurse-saul'),
      title: t('maturityGuide.nav.ttf', 'Temperature-Time Factor'),
      subtitle: t('maturityGuide.astm.ttfSubtitle', 'Nurse-Saul'),
      body: t('maturityGuide.astm.ttfCard', 'Adds up the temperature above a datum temperature over time. Simple and widely used for conventional concrete.'),
    },
    {
      key: 'ea',
      onClick: () => go('arrhenius'),
      title: t('maturityGuide.nav.ea', 'Equivalent Age'),
      subtitle: t('maturityGuide.astm.eaSubtitle', 'Arrhenius'),
      body: t('maturityGuide.astm.eaCard', 'Converts the temperature history into the age at a reference temperature. Better where curing temperatures are high or vary a lot.'),
    },
    {
      key: 'determine',
      onClick: () => go('determine'),
      title: t('maturityGuide.nav.determine', 'Determine Datum / Q'),
      subtitle: t('maturityGuide.astm.determineSubtitle', 'From lab data'),
      body: t('maturityGuide.astm.determineCard', 'Derive a mix-specific datum temperature and Q-value from mortar cube tests at three curing temperatures.'),
    },
  ];

  const row = (label: string, ttf: React.ReactNode, ea: React.ReactNode) => (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ fontWeight: 600, width: 180, verticalAlign: 'top' }}>{label}</TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>{ttf}</TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>{ea}</TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ maxWidth: 920 }}>
      <PageTitle>{t('maturityGuide.astm.title', 'ASTM C1074: Estimating Concrete Strength by the Maturity Method')}</PageTitle>
      <Para>
        {t(
          'maturityGuide.astm.p1',
          'ASTM C1074 is the standard practice for estimating in-place concrete strength from its measured temperature history. It rests on one principle: concrete of the same mix that reaches the same maturity index should have about the same strength, provided it has had enough moisture to keep hydrating.',
        )}
      </Para>

      <Heading>{t('maturityGuide.astm.partsTitle', 'The two parts of the method')}</Heading>
      <Box component="ol" sx={{ mt: 0, pl: 2.5 }}>
        <Typography component="li" variant="body2" sx={{ lineHeight: 1.75, mb: 1 }}>
          <strong>{t('maturityGuide.astm.part1Title', 'Strength-maturity relationship.')}</strong>{' '}
          {t(
            'maturityGuide.astm.part1Body',
            'Before field use, specimens of the nominated mix are made, their temperature is recorded, and they are tested for compressive strength at a series of ages (for example 1, 3, 7, 14 and 28 days). The strength results are plotted against the calculated maturity to give the relationship for that mix.',
          )}
        </Typography>
        <Typography component="li" variant="body2" sx={{ lineHeight: 1.75 }}>
          <strong>{t('maturityGuide.astm.part2Title', 'In-place maturity.')}</strong>{' '}
          {t(
            'maturityGuide.astm.part2Body',
            'Temperature sensors are placed at representative (and critical) locations in the structure and record from the start of curing. The temperature history is converted to maturity with the chosen function, and the relationship from part 1 gives the estimated strength at each sensor.',
          )}
        </Typography>
      </Box>

      <Heading>{t('maturityGuide.astm.functionsTitle', 'The two maturity functions')}</Heading>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small" aria-label={t('maturityGuide.astm.functionsTitle', 'The two maturity functions')}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>{t('maturityGuide.astm.colTtf', 'Temperature-Time Factor (Nurse-Saul)')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('maturityGuide.astm.colEa', 'Equivalent Age (Arrhenius)')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row(
              t('maturityGuide.astm.rowResult', 'Result'),
              t('maturityGuide.astm.ttfResult', 'Temperature-time factor, in °C·hours or °C·days'),
              t('maturityGuide.astm.eaResult', 'Equivalent age at a reference temperature, in hours or days'),
            )}
            {row(
              t('maturityGuide.astm.rowInputs', 'Input you set'),
              t('maturityGuide.astm.ttfInputs', 'Datum temperature (T0)'),
              t('maturityGuide.astm.eaInputs', 'Activation energy (or Q) and the reference temperature (Ts)'),
            )}
            {row(
              t('maturityGuide.astm.rowAssumes', 'Assumes'),
              t('maturityGuide.astm.ttfAssumes', 'Strength gain rate is a straight-line function of temperature'),
              t('maturityGuide.astm.eaAssumes', 'Strength gain rate rises exponentially with temperature'),
            )}
            {row(
              t('maturityGuide.astm.rowBest', 'Best suited for'),
              t('maturityGuide.astm.ttfBest', 'Conventional mixes and normal curing temperatures'),
              t('maturityGuide.astm.eaBest', 'Mass concrete, precast, steam or accelerated curing, and large temperature swings'),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Heading>{t('maturityGuide.astm.valuesTitle', 'Datum temperature and Q')}</Heading>
      <Para>
        {t(
          'maturityGuide.astm.valuesBody',
          'Both functions depend on a mix-dependent constant. ASTM C1074 gives approximate values for Type I cement without admixtures cured between 0 and 40 °C (a datum temperature of 0 °C and a Q of about 5000 K). Admixtures, supplementary cementitious materials and other cement types can change these values, so a value determined from lab data for the actual mix is preferred.',
        )}
      </Para>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <strong>{t('maturityGuide.astm.limitsTitle', 'Limitations.')}</strong>{' '}
        {t(
          'maturityGuide.astm.limitsBody',
          'The estimate is only valid for the calibrated mix and assumes the concrete stays moist enough to hydrate. It does not account for later-age strength loss after high early temperatures. Before critical operations such as formwork removal or post-tensioning, confirm the estimate with other tests.',
        )}
      </Alert>

      <Heading>{t('maturityGuide.astm.nextTitle', 'Continue')}</Heading>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {methodCards.map((c) => (
          <Card key={c.key} variant="outlined">
            <CardActionArea onClick={c.onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.title}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.subtitle}</Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.6 }}>{c.body}</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <PrevNext
        go={go}
        prev={{ label: t('maturityGuide.nav.introduction', 'Introduction'), to: 'introduction' }}
        next={{ label: t('maturityGuide.nav.ttf', 'Temperature-Time Factor'), to: 'nurse-saul' }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Method info (the groups table sits under each, in pages/MaturityInputs)
// ---------------------------------------------------------------------------

/** "Best suited for" as chips. */
const BestFor: React.FC<{ items: string[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, my: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
        {t('maturityGuide.bestFor', 'Best suited for:')}
      </Typography>
      {items.map((item) => (
        <Chip
          key={item}
          label={item}
          size="small"
          variant="outlined"
          sx={{ bgcolor: '#F1F7E6', borderColor: '#CFE3A8', color: 'primary.dark' }}
        />
      ))}
    </Box>
  );
};

/** Variables and worked example, folded away so the groups table stays near the top. */
const MoreDetail: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ArrowUpIcon /> : <ArrowDownIcon />}
        aria-expanded={open}
        sx={{ textTransform: 'none', px: 0 }}
      >
        {t('maturityGuide.moreDetail', 'Variables and worked example')}
      </Button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export const TemperatureTimeFactorInfo: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ maxWidth: 920 }}>
      <PageTitle>{t('maturityGuide.ttf.title', 'Temperature-Time Factor Method (Nurse-Saul)')}</PageTitle>
      <Para>
        {t(
          'maturityGuide.ttf.p1',
          'The Temperature-Time Factor method assumes concrete gains strength in proportion to the temperature it has accumulated over time. For each time interval it takes the average concrete temperature above the datum temperature, multiplies by the interval length, and adds the results up.',
        )}
      </Para>
      <TtfEquation />
      <BestFor
        items={[
          t('maturityGuide.ttf.best1', 'General construction'),
          t('maturityGuide.ttf.best2', 'Conventional mixes'),
          t('maturityGuide.ttf.best3', 'Normal curing temperatures (below about 40 °C)'),
          t('maturityGuide.ttf.best4', 'Everyday QC and strength estimation'),
        ]}
      />
      <MoreDetail>
        <VariableTable
          rows={[
            [<><Sym s="M" />(<Sym s="t" />)</>, t('maturityGuide.ttf.vM', 'temperature-time factor at age t (°C·hours or °C·days)')],
            [<Sym s="T" sub="a" />, t('maturityGuide.ttf.vTa', 'average concrete temperature during the interval (°C)')],
            [<Sym s="T" sub="0" />, t('maturityGuide.ttf.vT0', 'datum temperature, below which strength gain is taken as zero (°C)')],
            [<>Δ<Sym s="t" /></>, t('maturityGuide.ttf.vDt', 'time interval (hours or days)')],
          ]}
        />
        <Para>
          {t(
            'maturityGuide.ttf.clamp',
            'If the average temperature in an interval is below the datum temperature, that interval adds nothing (it never subtracts).',
          )}
        </Para>
        <Para>
          <strong>{t('maturityGuide.exampleTitle', 'Example:')}</strong>{' '}
          {t(
            'maturityGuide.ttf.exampleBody',
            'With a datum temperature of 0 °C, concrete that averages 20 °C for 24 hours gains 20 × 24 = 480 °C·hours.',
          )}
        </Para>
      </MoreDetail>
    </Box>
  );
};

export const EquivalentAgeInfo: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ maxWidth: 920 }}>
      <PageTitle>{t('maturityGuide.ea.title', 'Equivalent Age Method (Arrhenius)')}</PageTitle>
      <Para>
        {t(
          'maturityGuide.ea.p1',
          'The Equivalent Age method uses the Arrhenius equation, which recognises that the rate of cement hydration changes exponentially with temperature. Instead of adding up temperature, it converts the actual curing history into the age the concrete would have reached if it had been cured at a reference (specified) temperature.',
        )}
      </Para>
      <EaEquation />
      <BestFor
        items={[
          t('maturityGuide.ea.best1', 'Mass concrete'),
          t('maturityGuide.ea.best2', 'Precast'),
          t('maturityGuide.ea.best3', 'Steam or accelerated curing'),
          t('maturityGuide.ea.best4', 'High-performance concrete'),
          t('maturityGuide.ea.best5', 'Large temperature variations'),
        ]}
      />
      <MoreDetail>
        <VariableTable
          rows={[
            [<Sym s="t" sub="e" />, t('maturityGuide.ea.vTe', 'equivalent age at the reference temperature (hours or days)')],
            [<Sym s="Q" />, t('maturityGuide.ea.vQ', 'activation energy divided by the gas constant (K); Q = E / R, with R = 8.314 J/(mol·K)')],
            [<Sym s="T" sub="a" />, t('maturityGuide.ea.vTa', 'average concrete temperature during the interval (K)')],
            [<Sym s="T" sub="s" />, t('maturityGuide.ea.vTs', 'reference (specified) temperature (K)')],
            [<>Δ<Sym s="t" /></>, t('maturityGuide.ea.vDt', 'time interval (hours or days)')],
          ]}
        />
        <Para>
          {t(
            'maturityGuide.ea.kelvin',
            'Temperatures in the equation are absolute (kelvin = °C + 273). You enter them in °C; ConXedge converts them.',
          )}
        </Para>
        <Para>
          <strong>{t('maturityGuide.exampleTitle', 'Example:')}</strong>{' '}
          {t(
            'maturityGuide.ea.exampleBody',
            'With Q = 5000 K and a reference temperature of 20 °C, 24 hours at 30 °C counts as about 42 hours of equivalent age, and 24 hours at 10 °C counts as about 13 hours.',
          )}
        </Para>
      </MoreDetail>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// The page frame: grey side menu + selected section, laid out like Settings
// (pages/Settings.tsx): a flush full-height rail with icons, the section on a
// card beside it. Phones keep the rail, icons only.
// ---------------------------------------------------------------------------

const SECTION_ICONS: Record<MaturitySection, React.ReactElement> = {
  introduction: <IntroIcon fontSize="small" />,
  'astm-c1074': <StandardIcon fontSize="small" />,
  'nurse-saul': <TtfIcon fontSize="small" />,
  arrhenius: <EaIcon fontSize="small" />,
  determine: <LabIcon fontSize="small" />,
};

export const MaturitySidebarLayout: React.FC<{
  section: MaturitySection;
  onSelect: (section: MaturitySection) => void;
  children: React.ReactNode;
}> = ({ section, onSelect, children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Labels show beside the icons from md up, so the tooltip is only for phones.
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const contentRef = useRef<HTMLDivElement>(null);

  // Each section starts at the top, not at the previous one's scroll position.
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [section]);

  return (
    <Box
      sx={{
        display: 'flex',
        // Full height under the 64px app bar (the negative margins cancel main's padding).
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        minHeight: { xs: 'calc(100vh - 80px)', md: 0 },
        mx: -3,
        mt: -1.5,
        mb: { xs: 0, md: -3 },
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Box
        component="nav"
        aria-label={t('nav.maturityInputs', 'Concrete Maturity Inputs')}
        sx={{ width: { xs: 52, md: 248 }, flexShrink: 0, bgcolor: '#f0f0f0', overflow: 'auto' }}
      >
        <List disablePadding sx={{ px: { xs: 0.5, md: 1 }, pt: 1 }}>
          {MATURITY_SECTIONS.map((key) => {
            const selected = section === key;
            const label = sectionLabel(key, t);
            // TTF, EA and Datum / Q sit under ASTM C1074 (indented beside the labels).
            const sub = isAstmSubsection(key);
            return (
              <Tooltip key={key} title={label} placement="right" disableHoverListener={wide}>
                <ListItemButton
                  selected={selected}
                  onClick={() => onSelect(key)}
                  aria-label={label}
                  sx={{
                    py: 1,
                    px: { xs: 1, md: 1.5 },
                    ml: { xs: 0, md: sub ? 2 : 0 },
                    mb: 0.25,
                    borderRadius: '6px',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: 'text.primary' },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(0,0,0,0.08)',
                      color: 'text.primary',
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      borderRadius: '0 6px 6px 0',
                      pl: { xs: 0.625, md: 1.25 },
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 0, md: 28 }, color: 'inherit', alignSelf: 'flex-start', mt: '1px' }}>
                    {SECTION_ICONS[key]}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    sx={{ display: { xs: 'none', md: 'block' }, my: 0 }}
                    primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: selected ? 600 : 400, lineHeight: 1.35 }}
                  />
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Content area: scrolls internally on desktop, flows on mobile */}
      <Box ref={contentRef} sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', md: 'auto' }, p: { xs: 1.5, md: 2 } }}>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, minHeight: { md: '100%' } }}>{children}</Paper>
      </Box>
    </Box>
  );
};

export default MaturitySidebarLayout;
